import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import PlansDropdown from "./dropdowns/PlansDropdown";
import PlansModal from "./modals/PlansModal";
import { useMemo, useState } from "react";
import OperatorDropdown from "./dropdowns/OperatorDropdown";
import OperatorModal from "./modals/OperatorModal";
import FilterModal from "./modals/FilterModal";
import moment from "moment";
import MachineDetailsDropdown from "./dropdowns/MachineDetailsDropdown";
import LastFetchedDropdown from "./dropdowns/LastFetchedDropdown";
import secureLocalStorage from "react-secure-storage";

const MachineDashboard = () => {
    const privileges = secureLocalStorage.getItem("privileges");
    const [currentPlan, setCurrentPlan] = useState(null);
    const [currentOperator, setCurrentOperator] = useState(null);
    const [date, setDate] = useState(null);
    const [department, setDepartment] = useState("");
    const { useFetch } = useAPIQuery({
        key: "api/machine-plan",
        endpoint: "/api/machine-plan/",
    });
    const { data, isFetching } = useFetch({
        params: {
            date: date ?? moment().format("YYYY-MM-DD"),
            department: department ?? "",
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 300000,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const modifiedData = useMemo(() => {
        if (data?.results?.data) {
            return data?.results?.data?.map((d) => ({
                ...d,
                version_acc: d?.raw_data?.heartbeat_status?.VERSION,
                is_on_acc: d?.raw_data?.is_on,
                raw_data_acc: isNaN(Number(d?.raw_data?.raw_data))
                    ? 0
                    : Number(d?.raw_data?.raw_data),
                updated_manual_total_acc: isNaN(
                    Number(d?.raw_data?.updated_manual_total)
                )
                    ? 0
                    : Number(d?.raw_data?.updated_manual_total),
            }));
        }
        return [];
    }, [data]);

    const columns = [
        {
            accessor: "machine_name",
            header: "Machine Name",
            filterable: true,
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>{row?.machine_name}</span>
                    <MachineDetailsDropdown row={row} />
                </div>
            ),
            sortable: true,
        },
        {
            accessor: "total_actual_production",
            header: "Total Actual Production",
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "updated_manual_total_acc",
            header: "Manual Count",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>
                        {row?.plans?.length > 0
                            ? row.plans.reduce(
                                  (sum, p) =>
                                      sum + (p?.manual_production_count ?? 0),
                                  0
                              )
                            : 0}
                    </span>
                </div>
            ),
            sortable: true,
        },
        {
            accessor: "total_planned_production",
            header: "Total Planned Production",
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "raw_data_acc",
            header: "Cycles (shots)",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>{row?.raw_data?.raw_data}</span>
                    {row?.raw_data?.raw_data && (
                        <LastFetchedDropdown row={row} />
                    )}
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_heartbeat") && {
            accessor: "is_on_acc",
            header: "Heartbeat",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span
                        className={`badge badge-dot bg-${
                            row?.raw_data?.is_on === 0 ? "danger" : "success"
                        }`}
                    >
                        {row?.raw_data?.is_on === 0 ? "Off" : "On"}
                    </span>
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_heartbeat_version") && {
            accessor: "version_acc",
            header: "Version",
            cell: (row) => (
                <span>{row?.raw_data?.heartbeat_status?.VERSION ?? "-"}</span>
            ),
            sortable: true,
            filterable: true,
        },
        {
            accessor: "plans",
            header: "Plans",
            cell: (row) => (
                <>
                    {row?.plans?.length > 0 ? (
                        <PlansDropdown
                            row={row}
                            setCurrentPlan={setCurrentPlan}
                        />
                    ) : (
                        "-"
                    )}
                </>
            ),
        },
        {
            accessor: "operator",
            header: "Operator",
            cell: (row) => (
                <>
                    {row?.operators?.length > 0 ? (
                        <OperatorDropdown
                            row={row}
                            setCurrentOperator={setCurrentOperator}
                        />
                    ) : (
                        "-"
                    )}
                </>
            ),
        },
    ].filter(Boolean);

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex">
                        <h3 className="nk-block-title page-title">
                            {`Machine details for ${
                                date ?? moment().format("YYYY-MM-DD")
                            } in ${
                                department !== ""
                                    ? department + " department"
                                    : "All departments"
                            }`}
                            {}
                        </h3>
                    </div>
                    <div className="d-flex align-items-center justify-content-center ms-auto">
                        <button
                            className="btn btn-icon btn btn-outline-primary rounded-pill me-3"
                            data-bs-toggle="modal"
                            data-bs-target="#machine-detials-filter-modal"
                        >
                            <em className="icon ni ni-filter-fill" />
                        </button>
                    </div>
                </div>
            </div>
            <Table
                data={modifiedData ?? []}
                columns={columns}
                isLoading={isFetching}
                maxHeight={600}
                manualFilter={false}
                manualSorting={false}
                pagination={false}
                selectRows={false}
                globalSearch={false}
                filterModal={false}
                editRows={false}
                deleteRows={false}
            />
            <PlansModal plan={currentPlan} />
            <OperatorModal operator={currentOperator} />
            <FilterModal setDate={setDate} setDepartment={setDepartment} />
        </>
    );
};

export default MachineDashboard;
